import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/client';
import toast from 'react-hot-toast';
import StatusBadge from '../../components/shared/StatusBadge';
import {
  CheckCircleIcon,
  ClockIcon,
  ShieldCheckIcon,
  ArrowTopRightOnSquareIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { DATE_FLOOR, completionMax, expiryMax } from '../../lib/dateBounds';

// Admin Onboarding page — list of staff in onboarding + each one's full
// checklist with real upload + verify actions. Mirrors the StepRow pattern
// from CandidateDetailPage's Clearance tab so the UX is consistent.

export default function OnboardingPage() {
  const { user, isManagement } = useAuth();
  const [searchParams] = useSearchParams();
  const [staffList, setStaffList] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const preselectId = searchParams.get('user');
    if (isManagement) {
      // Always load the staff list. If a ?user=X param is present
      // (deep link from a candidate's Employment File), open straight
      // into that staff's checklist.
      loadStaff().then(() => {
        if (preselectId) loadOnboarding(Number(preselectId));
      });
    } else {
      loadOnboarding(user.id);
    }
    // eslint-disable-next-line
  }, []);

  const loadStaff = async () => {
    try {
      const { data } = await api.get('/staff?filter=current');
      setStaffList(data.filter(s => ['active', 'onboarding', 'pending_clearance', 'pending_hire'].includes(s.status)));
    } catch { toast.error('Failed to load staff'); }
    finally { setLoading(false); }
  };

  const loadOnboarding = async (userId) => {
    try {
      const { data } = await api.get(`/onboarding/${userId}`);
      setSteps(data);
      setSelectedUser(userId);
    } catch (err) {
      if (err.response?.status === 404) setSteps([]);
      else toast.error('Failed to load onboarding');
    } finally { setLoading(false); }
  };

  const initialize = async (userId) => {
    try {
      await api.post(`/onboarding/${userId}/initialize`);
      toast.success('Onboarding initialized');
      loadOnboarding(userId);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to initialize');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }

  // Staff member view: show own checklist
  if (!isManagement) {
    return (
      <div className="animate-slide-up">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">My Onboarding</h1>
        <p className="text-gray-500 mb-6">Track your onboarding progress.</p>
        <Checklist steps={steps} onChanged={() => loadOnboarding(user.id)} userId={user.id} readOnly />
      </div>
    );
  }

  return (
    <div className="animate-slide-up">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Onboarding</h1>
      <p className="text-gray-500 mb-6">Track and verify onboarding steps for new staff.</p>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Staff list */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <h2 className="font-semibold text-gray-900 mb-3">Staff Members</h2>
          <div className="space-y-1.5 max-h-[70vh] overflow-y-auto">
            {staffList.map(s => (
              <button key={s.id} onClick={() => loadOnboarding(s.id)}
                className={`w-full text-left p-2.5 rounded-lg transition ${
                  selectedUser === s.id ? 'bg-orange-50 text-orange-700' : 'hover:bg-gray-50'
                }`}>
                <p className="font-medium text-sm">{s.first_name} {s.last_name}</p>
                <p className="text-xs text-gray-500 capitalize">{s.role} · <StatusBadge status={s.status} small /></p>
              </button>
            ))}
            {staffList.length === 0 && (
              <p className="text-xs text-gray-400 py-4 text-center">No staff in onboarding right now.</p>
            )}
          </div>
        </div>

        {/* Checklist for selected staff */}
        <div>
          {!selectedUser ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
              <ClockIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">Pick a staff member to see their checklist.</p>
            </div>
          ) : steps.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
              <p className="text-gray-700 font-medium mb-3">No onboarding seeded for this staff member yet.</p>
              <button onClick={() => initialize(selectedUser)}
                className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2 rounded-lg">
                Initialize Onboarding
              </button>
            </div>
          ) : (
            <Checklist steps={steps} userId={selectedUser} onChanged={() => loadOnboarding(selectedUser)} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Checklist + StepRow ─────────────────────────────────────────────

function Checklist({ steps, userId, onChanged, readOnly = false }) {
  const verifiedCount = steps.filter(s => s.status === 'verified').length;
  const progress = steps.length > 0 ? Math.round((verifiedCount / steps.length) * 100) : 0;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-gray-900">Onboarding Checklist</h2>
        <span className="text-sm text-gray-500">{verifiedCount}/{steps.length} verified ({progress}%)</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2 mb-5">
        <div className="h-2 rounded-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
      </div>
      <div className="space-y-2">
        {steps.sort((a, b) => a.step_order - b.step_order).map((step) => (
          <StepRow key={step.id} step={step} userId={userId} onChanged={onChanged} readOnly={readOnly} />
        ))}
      </div>
    </div>
  );
}

function StepRow({ step, userId, onChanged, readOnly }) {
  const verified = step.status === 'verified';
  const completed = step.status === 'completed';
  const needsExpiration = !!step.requires_expiration_date;

  const [busy, setBusy] = useState(false);
  const [adminCompletionDate, setAdminCompletionDate] = useState('');
  const [adminExpirationDate, setAdminExpirationDate] = useState('');
  const fileInputRef = useRef(null);

  const openPicker = () => {
    if (needsExpiration && !adminExpirationDate) {
      toast.error(`Enter the expiration date first (valid ${step.validity_months || ''} months).`);
      return;
    }
    fileInputRef.current?.click();
  };

  const adminUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('document', file);
      fd.append('template_id', String(step.template_step_id));
      if (adminCompletionDate) fd.append('completion_date', adminCompletionDate);
      if (adminExpirationDate) fd.append('expiration_date', adminExpirationDate);
      // Phase 1.5: route through unified controller for audit logging.
      const { data } = await api.post(`/file/${userId}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data?.transitioned) toast.success(`Uploaded + status → ${data.status.replace(/_/g, ' ')}`);
      else toast.success('Uploaded and verified');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (needsExpiration && !adminExpirationDate && !step.expiration_date) {
      toast.error('Enter the expiration date before verifying.');
      return;
    }
    setBusy(true);
    try {
      const body = {};
      if (adminExpirationDate) body.expiration_date = adminExpirationDate;
      // Phase 1.5: unified verify endpoint, audit logged.
      const { data } = await api.post(
        `/file/${userId}/items/onboarding_progress/${step.id}/verify`, body);
      if (data?.transitioned) toast.success(`Status → ${data.status.replace(/_/g, ' ')}`);
      else toast.success('Verified');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to verify');
    } finally { setBusy(false); }
  };

  const markPendingComplete = async () => {
    setBusy(true);
    try {
      await api.patch(`/onboarding/steps/${step.id}/complete`);
      toast.success('Marked completed');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setBusy(false); }
  };

  const border = verified ? 'border-emerald-200 bg-emerald-50/30'
    : completed ? 'border-amber-200 bg-amber-50/30'
    : 'border-gray-200 bg-white';

  return (
    <div className={`border rounded-xl p-3 ${border}`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {verified ? <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
            : completed ? <ShieldCheckIcon className="w-5 h-5 text-amber-500" />
            : <ClockIcon className="w-5 h-5 text-gray-300" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
            <div className="font-medium text-gray-900 text-sm">{step.step_name}</div>
            <StatusBadge status={step.status} small />
          </div>
          {step.description && <p className="text-xs text-gray-600 leading-snug">{step.description}</p>}

          {step.external_url && (
            <a href={step.external_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-orange-600 hover:underline mt-2">
              <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" /> Open external site
            </a>
          )}
          {step.document_url && (
            <a href={step.document_url} target="_blank" rel="noreferrer"
              className="ml-3 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-2">
              <DocumentTextIcon className="w-3.5 h-3.5" /> View uploaded doc
            </a>
          )}

          {!verified && !readOnly && (
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <input type="date" min={DATE_FLOOR} max={completionMax()} value={adminCompletionDate}
                onChange={(e) => setAdminCompletionDate(e.target.value)}
                title="Completion date"
                className="bg-white border border-gray-200 rounded-lg px-2 py-1 text-xs" />
              {needsExpiration && (
                <input type="date" min={DATE_FLOOR} max={expiryMax()} value={adminExpirationDate}
                  onChange={(e) => setAdminExpirationDate(e.target.value)}
                  title={`Expiration — required, valid ${step.validity_months} months`}
                  className="bg-amber-50 border border-amber-300 rounded-lg px-2 py-1 text-xs"
                  placeholder="Exp. date" />
              )}
              <button type="button" onClick={openPicker} disabled={busy}
                className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/40 text-white text-xs font-medium px-3 py-1.5 rounded-lg">
                {busy ? 'Uploading…' : (step.uploader === 'admin' ? 'Upload result' : 'Upload doc')}
              </button>
              <input ref={fileInputRef} type="file"
                accept="image/*,application/pdf"
                style={{ display: 'none' }}
                onChange={adminUpload} />

              {completed && (
                <button onClick={verify} disabled={busy}
                  className="bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white text-xs font-medium px-3 py-1.5 rounded-lg">
                  Verify
                </button>
              )}
              {step.status === 'pending' && !step.requires_upload && (
                <button onClick={markPendingComplete} disabled={busy}
                  className="text-xs text-orange-600 hover:underline">
                  Mark complete (no doc)
                </button>
              )}
              {needsExpiration && (
                <p className="basis-full text-[11px] text-amber-700">
                  This step requires an expiration date (valid {step.validity_months} months).
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
